import React, { useState, useEffect, useCallback } from 'react';

import axios from 'axios';
import createSignedRequest from '../../../store/actions/createSignedRequest';
import { connect } from 'react-redux';
import { Actions as ADMINACTIONS } from '../../../store/actions/adminActions';
import _ from 'lodash';
import { getIndexCount } from '../../utils/GlobalFunctions';

import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../ui/pagination';
import { Alert, AlertTitle, AlertDescription } from '../../ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Spinner } from '../../ui/spinner';
const { apiURL } = require('../../../store/actions/apiURL');

const ClosestAirportListTable = ({
  branding,
  getAllItemsFromDB,
  closestAiport,
  tableName,
  page,
  filterSize,
  tableSize,
  setTableSize,
  setTotalItems,
  getRangeFromIndex,
  setPage,
  totalItems,
}) => {
  const [data, setData] = useState([]);
  const [filtering, setFiltering] = useState('');
  const [loading, setLoading] = useState(true);
  const totalPages = Math.ceil((totalItems || 0) / tableSize.value) || 1;

  const filteringOpenSearch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios(
        await createSignedRequest(
          'GET',
          `${apiURL}/admin/portal/filter/${tableName}${filtering ? `?query=${filtering}&page=${page}&size=${tableSize.value}` : ''}`,
        ),
      );
      setData(data?.items || []);
      setTotalItems(data?.itemCount || 0);
    } catch (error) {
      console.error('Error fetching filtered data:', error);
    } finally {
      setLoading(false);
    }
  }, [filtering, page, tableSize.value, tableName, setTotalItems]);

  const filterClosestAirport = useCallback(() => {
    setLoading(true);
    const filtered =
      closestAiport?.filter(
        (a) =>
          !filtering || [a.id, a.airport].some((f) => f?.toLowerCase().trim().includes(filtering?.toLowerCase().trim())),
      ) || [];

    if (filtered.length > 0 || filtering.length <= 4) {
      setData(filtered);
      setLoading(false);
    } else {
      filteringOpenSearch();
    }
  }, [closestAiport, filtering, filteringOpenSearch]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const indexCount = await getIndexCount(tableName);
        setTotalItems(indexCount);
        await getRangeFromIndex(tableName, tableSize.value, page);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [page, tableSize, getRangeFromIndex, setTotalItems, tableName]);

  useEffect(() => {
    filterClosestAirport();
    if (!filtering) {
      setTotalItems(null);
    }
  }, [closestAiport, filtering, filterClosestAirport, setTotalItems]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      if (setPage) {
        setPage(newPage);
      }
    }
  };

  const getPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    items.push(
      <PaginationItem key='first'>
        <PaginationLink onClick={() => handlePageChange(1)} isActive={page === 1}>
          1
        </PaginationLink>
      </PaginationItem>,
    );

    let startPage = Math.max(2, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

    if (startPage > 2) {
      items.push(
        <PaginationItem key='ellipsis-start'>
          <PaginationEllipsis />
        </PaginationItem>,
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink onClick={() => handlePageChange(i)} isActive={page === i}>
            {i}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    if (endPage < totalPages - 1) {
      items.push(
        <PaginationItem key='ellipsis-end'>
          <PaginationEllipsis />
        </PaginationItem>,
      );
    }

    if (totalPages > 1) {
      items.push(
        <PaginationItem key='last'>
          <PaginationLink onClick={() => handlePageChange(totalPages)} isActive={page === totalPages}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    return items;
  };

  if (data && !Array.isArray(data)) {
    return (
      <Card className='w-full'>
        <CardHeader>
          <CardTitle>Error</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant='destructive'>
            <AlertTitle>Error loading airport data</AlertTitle>
            <AlertDescription>{closestAiport || 'Unknown error occurred'}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='w-full shadow-md'>
      <CardHeader className='flex flex-row items-center justify-between space-y-0 px-6'>
        <CardTitle className='uppercase text-xl font-semibold'>Closest Airport</CardTitle>
        <div className='flex items-center space-x-2'>
          <Input
            className='max-w-xs'
            placeholder='Search airports...'
            value={filtering}
            onChange={(e) => setFiltering(e.target.value)}
          />
          <Select
            value={tableSize.value.toString()}
            onValueChange={(value) => {
              const selected = filterSize.find((option) => option.value.toString() === value);
              setTableSize(selected);
            }}>
            <SelectTrigger className='w-24'>
              <SelectValue placeholder='Rows' />
            </SelectTrigger>
            <SelectContent>
              {filterSize.map((option) => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      {loading && !data?.length ? (
        <CardContent className='px-6'>
          <div className='mt-4 w-full flex items-center justify-center p-8'>
            <Spinner size='large' />
          </div>
        </CardContent>
      ) : (
        <CardContent className='px-6'>
          {!loading && !data?.length ? (
            <Alert>
              <AlertTitle>No Match Found</AlertTitle>
              <AlertDescription>There are no search results!</AlertDescription>
            </Alert>
          ) : (
            <div className='rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='text-center'>ID</TableHead>
                    <TableHead className='text-center'>Airport</TableHead>
                    <TableHead className='text-center'>Latitude</TableHead>
                    <TableHead className='text-center'>Longitude</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((airport, index) => (
                    <TableRow key={airport.id || index}>
                      <TableCell className='font-medium justify-items-center'>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className='cursor-pointer truncate max-w-xs'>{airport.id}</div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className='p-1'>{airport.id}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className='text-center'>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className='cursor-pointer truncate max-w-xs'>{airport.airport}</div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className='p-1'>{airport.airport}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className='text-center'>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className='cursor-pointer truncate max-w-xs'>{airport.lat}</div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className='p-1'>{airport.lat}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className='text-center'>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className='cursor-pointer truncate max-w-xs'>{airport.lng}</div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className='p-1'>{airport.lng}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {data?.length > 0 && (
            <div className='mt-4 flex justify-end'>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => handlePageChange(page - 1)}
                      className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>

                  {getPaginationItems()}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => handlePageChange(page + 1)}
                      className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { page, setTableSize, tableSize, setPage, totalItems } = ownProps;
  const tableName = 'closestAirport';
  const sortedClosestAirport = _.sortBy(state?.admin?.indexItems?.items, ['airport']);

  return {
    closestAiport: sortedClosestAirport || state?.admin?.indexItems?.items,
    branding: state.branding,
    tableName,
    filterSize: state.admin?.filterSize,
    page,
    tableSize,
    setTableSize,
    setPage,
    totalItems,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getAllItemsFromDB: (table, filter) => dispatch(ADMINACTIONS.getAllItemsFromDB(table, filter)),
  toggleModal: (status, action, item, notAutoClose) =>
    dispatch(ADMINACTIONS.toggleModal(status, action, item, notAutoClose)),
  filterFromOpeanSearch: (index, query) => dispatch(ADMINACTIONS.filterFromOpeanSearch(index, query)),
  getRangeFromIndex: (index, size, from) => dispatch(ADMINACTIONS.getRangeFromIndex(index, size, from)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClosestAirportListTable);
